import React, {useRef, useState} from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import PropTypes from 'prop-types';
import Form from "react-bootstrap/Form";
import nullUserInfo from "./UserInfo";

const patientRole = 'patient'

// ---------------------------------------------------------------------------
/// This Modal dialog doesn't work over dialogs from material ui ('@mui/material/Dialog')
/// The window remain behind (Material UI dialog z-Index=1100)

function UserActionDialog(props) {
  const { windowTitle, show, userType, hasEmail, hasUsername, processFormData, handleFormExit, defaultUserInfo } = props
  const initialValues = (defaultUserInfo) ? defaultUserInfo : nullUserInfo;

  const [ validated, setValidated ] = useState(false);
  const [ message, setMessage ] = useState({text:'', isError: false});
  const [ username, setUsername ] = useState(initialValues.username);
  const [ givenName, setGivenName ] = useState(initialValues.givenName);
  const [ familyName, setFamilyName ] = useState(initialValues.familyName);
  const [ email, setEmail ] = useState(initialValues.email);
  const [ isActive, setIsActive ] = useState(initialValues.isActive);
  const [ sendEmail, setSendEmail ] = useState(userType !== patientRole);
  const [ isFormDisabled, setIsFormDisabled ] = useState(false);
  const [ isCancelDisabled, setIsCancelDisabled ] = useState(false);

  const userFormRef = useRef();

  const handleUpdateUsername = (e) => {e.preventDefault(); setUsername(e.target.value);}
  const handleUpdateGivenName = (e) => {e.preventDefault(); setGivenName(e.target.value);}
  const handleUpdateFamilyName = (e) => {e.preventDefault(); setFamilyName(e.target.value);}
  const handleUpdateEmail = (e) => {e.preventDefault(); setEmail(e.target.value);}
  const handleUpdateIsActive = (e) => {setIsActive(e.target.checked);}
  const handleUpdateSendEmail = (e) => {setSendEmail(e.target.checked);}

  const handleSubmitEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setValidated(true);

    const form = event.currentTarget;
    if (form.checkValidity()) {
      setIsFormDisabled(true);
      setIsCancelDisabled(true);
      setMessage({text: 'Sending...', isError: false});

      const form_data = {
        id: initialValues.id,
        userType: userType,
        username: username,
        givenName: givenName,
        familyName: familyName,
        email: email,
        isActive: isActive,
        sendEmail: sendEmail
      };
      processFormData(form_data)
        .then((response) => {
          setMessage({text: response, isError: false})
          setIsCancelDisabled(false);
          if (initialValues.onSuccessCallback) {
            initialValues.onSuccessCallback();
          }
        })
        .catch((error) => {
          setMessage({text: `${error}`, isError: true})
          setIsFormDisabled(false);
          setIsCancelDisabled(false);
        });
      // clear();
    } else {
      setMessage({text: 'Error. Please correct fields above.', isError: true});
    }
  };

  const handleCancelEvent  = (event) => {
    event.preventDefault();
    event.stopPropagation();

    clear();
    handleFormExit();
  };

  const clear = () => {
    setValidated(false);
    setGivenName('');
    setFamilyName('');
    setEmail('');
    setIsActive(true);
    setSendEmail(true);
    setMessage({text: '', isError: false});

    userFormRef.current.reset();
  };

  // Note on the namePattern RegEx.
  // Let namechar := [\p{L}\d] be any Unicode letter (lower or upper case) or digit.
  // Then, namePattern accepts:
  //  - "namechar + zero or more [hyphen, namechar, underscore, space] + namechar" for a minimum length of 2 chars, OR
  //  - A single "namechar"
  const namePattern = String.raw`[\p{L}\d][\-\p{L}\d_ .']*[\p{L}\d.]|[\p{L}\d][.]?`;  // "letter + [maybe hyphen, letter, space] + letter" OR "exactly 1 letter"
  const emailPattern = String.raw`[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$`
  const usernamePattern = String.raw`^[\w.]*$`

  return(
    <Modal show={show} size="lg">

      <Modal.Header>
        <h1>{windowTitle}</h1>
      </Modal.Header>
      <Modal.Body>
        <Form id='user-form' ref={userFormRef} noValidate validated={validated} onSubmit={handleSubmitEvent}>
          <Form.Group className="mb-3">
            <Form.Label xs={2}>User type:</Form.Label>
            <Form.Control type="text" readOnly disabled defaultValue={userType} />
          </Form.Group>
          {hasUsername &&
            <Form.Group className="mb-3">
              <Form.Label>Username:</Form.Label>
              <Form.Control type="text"
                            placeholder="Enter 6 or more characters"
                            defaultValue={username}
                            onChange={handleUpdateUsername}
                            pattern={usernamePattern}
                            required={true}
                            disabled={isFormDisabled} />
            </Form.Group>
          }
          <Form.Group className="mb-3">
            <Form.Label>Given name:</Form.Label>
            <Form.Control type="text"
                          placeholder="Enter the user's given name"
                          defaultValue={givenName}
                          onChange={handleUpdateGivenName}
                          pattern={namePattern}
                          required={true}
                          disabled={isFormDisabled} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Family name:</Form.Label>
            <Form.Control type="text"
                          placeholder="Enter the user's family name"
                          defaultValue={familyName}
                          onChange={handleUpdateFamilyName}
                          pattern={namePattern}
                          required={true}
                          disabled={isFormDisabled} />
          </Form.Group>
          {hasEmail &&
            <Form.Group className="mb-3">
              <Form.Label>Email address:</Form.Label>
              <Form.Control type="email"
                            placeholder="Enter a valid email address"
                            defaultValue={email}
                            onChange={handleUpdateEmail}
                            pattern={emailPattern}
                            required={true}
                            disabled={isFormDisabled} />
            </Form.Group>
          }
          <Form.Group className="mb-3">
            <Form.Check type="checkbox" label="User is active" defaultChecked={isActive} onChange={handleUpdateIsActive} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check type="checkbox" label="Send email to user upon creation" defaultChecked={sendEmail} onChange={handleUpdateSendEmail} />
          </Form.Group>
        </Form>
        <div>
          {message.text && ! message.isError && <div className={"normal_message"}>{message.text}</div> }
          {message.text && message.isError && <div className={"error_message"}>{message.text}</div> }
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button color="success" type="submit" form='user-form' disabled={isFormDisabled}>Submit</Button>
        <Button color="failure" onClick={handleCancelEvent} disabled={isCancelDisabled}>Cancel</Button>
      </Modal.Footer>
    </Modal>
  );
}

UserActionDialog.propTypes = {
  windowTitle: PropTypes.string.isRequired,
  show: PropTypes.bool,
  userType: PropTypes.string, // Do not use title. "Title" is a regular attribute used to display tooltip help
  hasEmail: PropTypes.bool,
  hasUsername: PropTypes.bool,
  defaultUserInfo: PropTypes.object.isRequired,
  // using props named "on..." cause React to warn about Unknown Prop ex: onConfirm
  handleFormExit: PropTypes.func.isRequired,
  processFormData: PropTypes.func.isRequired,
}

export default UserActionDialog;
